import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../components/layout/SiteShell";
import ContentPageLayout from "../components/content/ContentPageLayout";
import SEO from "../components/seo/SEO";
import ConversionCtas from "../components/content/ConversionCtas";
import WellnessDisclaimer from "../components/content/WellnessDisclaimer";
import PremiumPlaceholder from "../components/content/PremiumPlaceholder";
import DarkCard from "../components/layout/DarkCard";
import { BRAND_BANNER_IMAGE, BRAND_CARD_IMAGE } from "../lib/data";

const sections = [
  {
    title: "A family-run UK brand",
    body: "Moss Matrix is a family-run UK brand built from lived experience, curiosity, and respect for natural ingredients. We are community-focused and inspired by the simple routines we actually use at home — not hype cycles.",
  },
  {
    title: "Careful fresh preparation",
    body: "We blend sea moss gel in small runs so texture stays consistent. Batches leave cold, move quickly, and arrive with storage guidance that respects the work inside the jar. We prepare carefully rather than rushing, especially because this is a fresh product.",
  },
  {
    title: "Gold and Purple bases",
    body: "Gold is our smooth, mild classic. Purple Sea Moss is our deeper base: naturally darker in colour and priced higher due to sourcing and availability. Rotate for variety. Neither is positioned as medically stronger.",
  },
  {
    title: "Clear ordering, calm education",
    body: "Plain Gold and Purple keep ordering simple. Mix-ins and botanicals stay educational on the site while we keep kitchen prep manageable. Watch the product, choose your size, then checkout.",
  },
  {
    title: "Natural wellness guidance, not miracle claims",
    body: "We share herbal wellness interest and community-style education rooted in traditional knowledge. We do not claim regulated medical titles unless credentials are published and permitted. Simple daily routines can feel grounding when people take food, movement, rest, and natural ingredients seriously — without turning that into a cure story.",
  },
  {
    title: "Honest wellness",
    body: "We believe in honest wellness: no miracle claims, no pressure, just fresh sea moss, carefully prepared, and clear education. If a line on this site ever sounds like it cures, treats, or detoxes you, tell us and we will rewrite it.",
  },
];

export default function AboutPage() {
  return (
    <SiteShell theme="dark">
      <SEO
        title="About Moss Matrix | Family-run UK sea moss gel"
        description="About Moss Matrix: a family-run UK sea moss gel brand, careful fresh preparation, gold and purple bases, and calm education without miracle claims."
        path="/about"
      />
      <ContentPageLayout
        eyebrow="About"
        title="Small-batch gel, rooted in real kitchens."
        subtitle="Premium, to us, means accountable: calm copy, honest labels, and respect for how people actually live. UK orders only at this stage; we grow thoughtfully from here."
      >
        <figure className="mb-14 max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-[1.25rem] ring-1 ring-white/15 bg-[#f7f3eb] shadow-[0_24px_56px_-32px_rgba(0,0,0,0.45)]">
            <img
              src={BRAND_BANNER_IMAGE}
              alt="Moss Matrix — Pure. Raw. Wildcrafted. From Nature to You"
              className="block w-full h-auto"
              width={1600}
              height={900}
              loading="eager"
              decoding="async"
            />
          </div>
          <figcaption className="mt-4 text-sm text-sage text-center max-w-2xl mx-auto leading-relaxed">
            Pure. Raw. Wildcrafted. — brand creative that used to sit on the homepage. Ordering stays on Shop; this page is
            for the story.
          </figcaption>
        </figure>

        <figure className="mb-14 max-w-4xl mx-auto">
          <DarkCard className="!p-3 md:!p-4 overflow-hidden">
            <div className="rounded-[1.1rem] overflow-hidden bg-[#1f2c25] aspect-[4/3] md:aspect-[16/10]">
              <img
                src={BRAND_CARD_IMAGE}
                alt="Moss Matrix brand card with floral line-art illustration on a wooden plate"
                className="w-full h-full object-cover editorial-img"
              />
            </div>
          </DarkCard>
          <figcaption className="mt-4 text-sm text-sage text-center max-w-2xl mx-auto leading-relaxed">
            Early Moss Matrix brand artwork — floral line art from our packaging and identity work. The site sells
            fridge-ready sea moss gel as a food product; we keep education calm and avoid miracle claims.
          </figcaption>
        </figure>

        <div className="grid md:grid-cols-2 gap-5 mb-14">
          {sections.map((s) => (
            <section key={s.title} className="home-glass-card rounded-2xl p-6">
              <h2 className="font-sans font-semibold text-xl text-ivory m-0">{s.title}</h2>
              <p className="mt-3 text-sm text-sage leading-relaxed m-0">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-14 max-w-5xl mx-auto">
          <PremiumPlaceholder label="Lifestyle photo: morning shake routine" />
          <PremiumPlaceholder label="Product photo: jar on stone surface" caption="Fresh batches, UK prepared." />
        </div>

        <WellnessDisclaimer className="max-w-3xl mx-auto mb-12" />

        <div className="rounded-3xl border border-white/14 bg-panel/80 p-8 md:p-10 text-center max-w-2xl mx-auto mb-12">
          <p className="font-sans font-semibold text-2xl text-ivory leading-snug m-0">
            “Premium” means we answer emails, fix mistakes, and admit when a harvest tastes a little different year to year.
          </p>
          <p className="mt-4 text-sm text-sage m-0">Moss Matrix studio notes</p>
        </div>

        <ConversionCtas />

        <p className="text-sm text-sage text-center mt-10">
          <Link to="/reviews" className="underline hover:text-goldhover">
            Reviews
          </Link>
          {" · "}
          <Link to="/contact" className="underline hover:text-goldhover">
            Contact
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
